import { Injectable, inject, resource, computed } from '@angular/core';
import { doc, getDoc, updateDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { FIRESTORE_TOKEN } from '../firebase/firestore.provider';
import { AuthService } from '../auth/auth.service';
import { Organization, TemperatureThresholds, WbgtThresholds } from '../../shared/interfaces/organization.interface';
import { hasMinRole } from '../auth/role.model';

export interface OrganizationSettingsUpdateInput {
  name?: string;
  wbgtThresholds?: WbgtThresholds | null;
  temperatureThresholds?: TemperatureThresholds | null;
  defaultMapCenter?: { lat: number; lng: number } | null;
  defaultMapZoom?: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private readonly firestore = inject(FIRESTORE_TOKEN);
  private readonly authService = inject(AuthService);

  private readonly tenantResource = resource({
    params: () => this.authService.userOrganizationId(),
    loader: async ({ params: orgId }) => {
      if (!orgId) {
        return null;
      }
      try {
        const orgRef = doc(this.firestore, 'organizations', orgId);
        const orgSnap = await getDoc(orgRef);

        if (orgSnap.exists()) {
          const data = orgSnap.data();
          return {
            id: orgSnap.id,
            name: data['name'],
            enabledFeatures: data['enabledFeatures'] || [],
            createdAt: data['createdAt'],
            updatedAt: data['updatedAt'],
            wbgtThresholds: data['wbgtThresholds'],
            temperatureThresholds: data['temperatureThresholds'],
            defaultMapCenter: data['defaultMapCenter'],
            defaultMapZoom: data['defaultMapZoom']
          } as Organization;
        } else {
          throw new Error('テナント情報が見つかりません');
        }
      } catch (err) {
        console.error('テナント情報の取得に失敗しました:', err);
        if (err instanceof Error && err.message === 'テナント情報が見つかりません') {
          throw err;
        }
        throw new Error('テナント情報の取得に失敗しました');
      }
    }
  });

  public readonly organization = computed(() =>
    this.tenantResource.hasValue() ? this.tenantResource.value() : null
  );
  public readonly isLoading = computed(() => this.tenantResource.status() === 'loading' || this.tenantResource.status() === 'reloading');
  public readonly error = computed(() => {
    const err = this.tenantResource.error();
    return err instanceof Error ? err.message : null;
  });

  public hasFeature(featureId: string): boolean {
    const org = this.organization();
    if (!org) return false;
    return org.enabledFeatures.includes(featureId);
  }

  public async updateMapSettings(center: { lat: number; lng: number }, zoom: number): Promise<void> {
    const orgId = this.authService.userOrganizationId();
    if (!orgId) {
      throw new Error('組織が選択されていません');
    }

    if (!hasMinRole(this.authService.userRole(), 'org_admin')) {
      throw new Error('権限がありません');
    }

    try {
      const orgRef = doc(this.firestore, 'organizations', orgId);
      await updateDoc(orgRef, {
        defaultMapCenter: center,
        defaultMapZoom: zoom,
        updatedAt: serverTimestamp()
      });
      this.tenantResource.reload();
    } catch (err) {
      console.error('マップ設定の保存に失敗しました:', err);
      throw new Error('マップ設定の保存に失敗しました');
    }
  }

  public async updateOrganizationSettings(data: OrganizationSettingsUpdateInput): Promise<void> {
    const orgId = this.authService.userOrganizationId();
    if (!orgId) {
      throw new Error('組織が選択されていません');
    }

    if (this.authService.userRole() !== 'org_admin') {
      throw new Error('権限がありません');
    }

    const updateData = Object.fromEntries(
      Object.entries({
        name: data.name,
        wbgtThresholds: data.wbgtThresholds,
        temperatureThresholds: data.temperatureThresholds,
        defaultMapCenter: data.defaultMapCenter,
        defaultMapZoom: data.defaultMapZoom,
        updatedAt: serverTimestamp()
      })
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, value === null ? deleteField() : value])
    );

    try {
      const orgRef = doc(this.firestore, 'organizations', orgId);
      await updateDoc(orgRef, updateData);
      this.tenantResource.reload();
    } catch (err) {
      console.error('組織設定の保存に失敗しました:', err);
      throw new Error('組織設定の保存に失敗しました');
    }
  }
}
